import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Colorize } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const dayLabels = [
  { key: "monday" },
  { key: "tuesday" },
  { key: "wednesday" },
  { key: "thursday" },
  { key: "friday" },
  { key: "saturday" },
  { key: "sunday" },
];

const defaultBusinessHours = [
  { day: "monday", enabled: true, start: "08:00", end: "18:00" },
  { day: "tuesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "wednesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "thursday", enabled: true, start: "08:00", end: "18:00" },
  { day: "friday", enabled: true, start: "08:00", end: "18:00" },
  { day: "saturday", enabled: false, start: "08:00", end: "12:00" },
  { day: "sunday", enabled: false, start: "08:00", end: "12:00" },
];

const parseBusinessHours = rawValue => {
  if (!rawValue) {
    return defaultBusinessHours;
  }

  try {
    const parsed = typeof rawValue === "string" ? JSON.parse(rawValue) : rawValue;
    if (!Array.isArray(parsed)) {
      return defaultBusinessHours;
    }

    return dayLabels.map(day => {
      const found = parsed.find(item => item.day === day.key);
      return found || defaultBusinessHours.find(item => item.day === day.key);
    });
  } catch (error) {
    return defaultBusinessHours;
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 700,
  },
  hoursTable: {
    "& td, & th": {
      whiteSpace: "nowrap",
    },
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75)
  }
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  description: Yup.string().nullable(),
  greetingMessage: Yup.string(),
  tagIds: Yup.array().of(Yup.string()),
  useOwnBusinessHours: Yup.boolean(),
  outOfHoursMessage: Yup.string(),
  outOfHoursAction: Yup.string().oneOf(["keep_pending", "close_ticket"]),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    color: "",
    description: "",
    greetingMessage: "",
    tagIds: [],
    useOwnBusinessHours: false,
    businessHours: defaultBusinessHours,
    outOfHoursMessage: "",
    outOfHoursAction: "keep_pending",
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const [availableTags, setAvailableTags] = useState([]);
  const greetingRef = useRef();

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue(prevState => {
          const parsedBusinessHours = parseBusinessHours(data.businessHours);
          return {
            ...prevState,
            ...data,
            description: data.description || "",
            tagIds: Array.isArray(data.tags)
              ? data.tags.map(tag => String(tag.id))
              : [],
            useOwnBusinessHours: Boolean(data.useOwnBusinessHours),
            businessHours: parsedBusinessHours,
            outOfHoursMessage: data.outOfHoursMessage || "",
            outOfHoursAction: data.outOfHoursAction || "keep_pending",
          };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
    };
  }, [queueId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    (async () => {
      try {
        const { data } = await api.get("/tags");
        setAvailableTags(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
  };

  const handleSaveQueue = async values => {
    try {
      const payload = {
        ...values,
        tagIds: Array.isArray(values.tagIds)
          ? values.tagIds
              .map(tagId => Number(tagId))
              .filter(tagId => Number.isInteger(tagId) && tagId > 0)
          : [],
        businessHours: JSON.stringify(
          Array.isArray(values.businessHours)
            ? values.businessHours
            : defaultBusinessHours
        ),
      };

      if (queueId) {
        await api.put(`/queue/${queueId}`, payload);
      } else {
        await api.post("/queue", payload);
      }
      toast.success(i18n.t("queueModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleBusinessHourChange = (
    setFieldValue,
    values,
    dayKey,
    field,
    value
  ) => {
    const nextHours = (values.businessHours || defaultBusinessHours).map(item =>
      item.day === dayKey ? { ...item, [field]: value } : item
    );
    setFieldValue("businessHours", nextHours);
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="md" fullWidth>
        <DialogTitle>
          {queueId
            ? `${i18n.t("queueModal.title.edit")} - ID #${queueId}`
            : `${i18n.t("queueModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={queue}
          enableReinitialize={true}
          validationSchema={QueueSchema}
          onSubmit={async (values, actions) => {
            await handleSaveQueue(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("queueModal.form.name")}
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                />
                <Field
                  as={TextField}
                  label={i18n.t("queueModal.form.color")}
                  name="color"
                  id="color"
                  onFocus={() => {
                    setColorPickerModalOpen(true);
                    greetingRef.current.focus();
                  }}
                  error={touched.color && Boolean(errors.color)}
                  helperText={touched.color && errors.color}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div
                          style={{ backgroundColor: values.color }}
                          className={classes.colorAdorment}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton
                        size="small"
                        color="default"
                        onClick={() => setColorPickerModalOpen(true)}
                      >
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                  margin="dense"
                />
                <ColorPicker
                  open={colorPickerModalOpen}
                  handleClose={() => setColorPickerModalOpen(false)}
                  onChange={color => {
                    setFieldValue("color", color);
                  }}
                />
                <div>
                <Field
                  as={TextField}
                  label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    inputRef={greetingRef}
                    rows={5}
                    fullWidth
                    name="greetingMessage"
                    error={
                      touched.greetingMessage && Boolean(errors.greetingMessage)
                    }
                    helperText={
                      (touched.greetingMessage && errors.greetingMessage) ||
                      i18n.t("queueModal.form.variablesHelp")
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>

                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.description")}
                    multiline
                    rows={4}
                    fullWidth
                    name="description"
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    variant="outlined"
                    margin="dense"
                  />
                </div>

                <FormControl
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  className={classes.formControl}
                >
                  <InputLabel id="queue-tags-select-label">
                    {i18n.t("queueModal.form.tags")}
                  </InputLabel>
                  <Select
                    labelId="queue-tags-select-label"
                    multiple
                    value={values.tagIds || []}
                    onChange={event =>
                      setFieldValue("tagIds", event.target.value)
                    }
                    label={i18n.t("queueModal.form.tags")}
                    renderValue={selected => (
                      <div className={classes.chipsWrap}>
                        {availableTags
                          .filter(tag => selected.includes(String(tag.id)))
                          .map(tag => (
                            <Chip
                              key={tag.id}
                              size="small"
                              label={tag.name}
                              style={{
                                backgroundColor: `${tag.color}22`,
                                color: tag.color,
                                fontWeight: 700
                              }}
                            />
                          ))}
                      </div>
                    )}
                  >
                    {availableTags.map(tag => (
                      <MenuItem key={tag.id} value={String(tag.id)}>
                        <Chip
                          size="small"
                          label={tag.name}
                          style={{
                            backgroundColor: `${tag.color}22`,
                            color: tag.color,
                            fontWeight: 700
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  {i18n.t("queueModal.form.businessHoursTitle")}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(values.useOwnBusinessHours)}
                      onChange={event =>
                        setFieldValue("useOwnBusinessHours", event.target.checked)
                      }
                    />
                  }
                  label={i18n.t("queueModal.form.useOwnBusinessHours")}
                />

                {values.useOwnBusinessHours ? (
                  <>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={3}
                      className={classes.formControl}
                      label={i18n.t("queueModal.form.outOfHoursMessage")}
                      value={values.outOfHoursMessage || ""}
                      onChange={event =>
                        setFieldValue("outOfHoursMessage", event.target.value)
                      }
                      helperText={i18n.t("queueModal.form.outOfHoursMessageHelp")}
                    />

                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <InputLabel>{i18n.t("queueModal.form.outOfHoursAction")}</InputLabel>
                      <Select
                        value={values.outOfHoursAction || "keep_pending"}
                        onChange={event =>
                          setFieldValue("outOfHoursAction", event.target.value)
                        }
                        label={i18n.t("queueModal.form.outOfHoursAction")}
                      >
                        <MenuItem value="keep_pending">{i18n.t("queueModal.form.keepPending")}</MenuItem>
                        <MenuItem value="close_ticket">{i18n.t("queueModal.form.closeTicket")}</MenuItem>
                      </Select>
                    </FormControl>

                    <Table size="small" className={classes.hoursTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t("queueModal.form.day")}</TableCell>
                          <TableCell>{i18n.t("queueModal.form.active")}</TableCell>
                          <TableCell>{i18n.t("queueModal.form.start")}</TableCell>
                          <TableCell>{i18n.t("queueModal.form.end")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dayLabels.map(day => {
                          const currentDay =
                            (values.businessHours || []).find(
                              item => item.day === day.key
                            ) ||
                            defaultBusinessHours.find(item => item.day === day.key);

                          return (
                            <TableRow key={day.key}>
                              <TableCell>{i18n.t(`weekdays.${day.key}`)}</TableCell>
                              <TableCell>
                                <Switch
                                  color="primary"
                                  checked={Boolean(currentDay?.enabled)}
                                  onChange={event =>
                                    handleBusinessHourChange(
                                      setFieldValue,
                                      values,
                                      day.key,
                                      "enabled",
                                      event.target.checked
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="time"
                                  variant="outlined"
                                  size="small"
                                  value={currentDay?.start || "08:00"}
                                  onChange={event =>
                                    handleBusinessHourChange(
                                      setFieldValue,
                                      values,
                                      day.key,
                                      "start",
                                      event.target.value
                                    )
                                  }
                                  inputProps={{ step: 300 }}
                                  disabled={!currentDay?.enabled}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="time"
                                  variant="outlined"
                                  size="small"
                                  value={currentDay?.end || "18:00"}
                                  onChange={event =>
                                    handleBusinessHourChange(
                                      setFieldValue,
                                      values,
                                      day.key,
                                      "end",
                                      event.target.value
                                    )
                                  }
                                  inputProps={{ step: 300 }}
                                  disabled={!currentDay?.enabled}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("queueModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {queueId
                    ? `${i18n.t("queueModal.buttons.okEdit")}`
                    : `${i18n.t("queueModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QueueModal;
